import {
  EN_ACTION_TYPES,
  EN_CLUB_KIT_NAMES,
  EN_AGE_GROUPS,
  EN_APPOINTMENT_ROLES,
  EN_GUARDIAN_RELATIONSHIPS,
  EN_KIT_DESIGNS,
  EN_KIT_RANKS,
  EN_MATCH_KIT_CHOICES,
  EN_MATCH_STATUSES,
  EN_PLAYER_FEET,
  EN_PLAYER_POSITIONS,
  EN_SEXES,
  EN_SQUAD_COACH_NOUN,
  EN_SQUAD_PLAYER_NOUNS,
  EN_SUBSCRIPTION_STATUSES,
  EN_TEAM_GENDERS,
  EN_TEAM_SHEET_STAFF_ROLES,
  EN_UNKNOWN_VALUE,
} from './en/domain.ts'
import {
  IT_ACTION_TYPES,
  IT_CLUB_KIT_NAMES,
  IT_AGE_GROUPS,
  IT_APPOINTMENT_ROLES,
  IT_GUARDIAN_RELATIONSHIPS,
  IT_KIT_DESIGNS,
  IT_KIT_RANKS,
  IT_MATCH_KIT_CHOICES,
  IT_MATCH_STATUSES,
  IT_PLAYER_FEET,
  IT_PLAYER_POSITIONS,
  IT_SEXES,
  IT_SQUAD_COACH_NOUN,
  IT_SQUAD_PLAYER_NOUNS,
  IT_SUBSCRIPTION_STATUSES,
  IT_TEAM_GENDERS,
  IT_TEAM_SHEET_STAFF_ROLES,
  IT_UNKNOWN_VALUE,
} from './it/domain.ts'
import type {
  ActionType,
  AgeGroup,
  KitDesign,
  KitRank,
  PlayerFoot,
  PlayerPosition,
  Sex,
  TeamGender,
} from './en/domain.ts'
import { locale } from './locale.ts'

// Domain enums in the active language. No enum is ever shown raw (guardrail §5):
// a value outside the catalogue gives UNKNOWN_VALUE, not itself.
export type {
  ActionType,
  AgeGroup,
  AppointmentRole,
  CountedNoun,
  GuardianRelationship,
  KitDesign,
  KitRank,
  MatchKitChoice,
  MatchStatus,
  PlayerFoot,
  PlayerPosition,
  PositionLabel,
  Sex,
  SubscriptionStatus,
  TeamGender,
} from './en/domain.ts'

const EN = locale === 'en'

export const ACTION_TYPES = EN ? EN_ACTION_TYPES : IT_ACTION_TYPES
export const APPOINTMENT_ROLES = EN ? EN_APPOINTMENT_ROLES : IT_APPOINTMENT_ROLES
export const PLAYER_POSITIONS = EN ? EN_PLAYER_POSITIONS : IT_PLAYER_POSITIONS
export const PLAYER_FEET = EN ? EN_PLAYER_FEET : IT_PLAYER_FEET
export const SEXES = EN ? EN_SEXES : IT_SEXES
export const MATCH_STATUSES = EN ? EN_MATCH_STATUSES : IT_MATCH_STATUSES
export const SUBSCRIPTION_STATUS_LABELS = EN ? EN_SUBSCRIPTION_STATUSES : IT_SUBSCRIPTION_STATUSES
export const AGE_GROUPS = EN ? EN_AGE_GROUPS : IT_AGE_GROUPS
export const GUARDIAN_RELATIONSHIPS = EN ? EN_GUARDIAN_RELATIONSHIPS : IT_GUARDIAN_RELATIONSHIPS
export const TEAM_GENDERS = EN ? EN_TEAM_GENDERS : IT_TEAM_GENDERS
export const SQUAD_PLAYER_NOUNS = EN ? EN_SQUAD_PLAYER_NOUNS : IT_SQUAD_PLAYER_NOUNS
export const SQUAD_COACH_NOUN = EN ? EN_SQUAD_COACH_NOUN : IT_SQUAD_COACH_NOUN
export const KIT_RANKS = EN ? EN_KIT_RANKS : IT_KIT_RANKS
export const KIT_DESIGNS = EN ? EN_KIT_DESIGNS : IT_KIT_DESIGNS
export const MATCH_KIT_CHOICES = EN ? EN_MATCH_KIT_CHOICES : IT_MATCH_KIT_CHOICES
export const CLUB_KIT_NAMES = EN ? EN_CLUB_KIT_NAMES : IT_CLUB_KIT_NAMES
export const TEAM_SHEET_STAFF_ROLES = EN ? EN_TEAM_SHEET_STAFF_ROLES : IT_TEAM_SHEET_STAFF_ROLES

// The order the call-up offers the società's kits in, a `satisfies` map like the designs' below,
// so a rank the contract adds cannot be missing from the choice.
const KIT_RANK_POSITION = { first: 0, second: 1 } satisfies Record<KitRank, number>

export const KIT_RANK_ORDER = Object.keys(KIT_RANK_POSITION) as readonly KitRank[]

// The picker's order, plain to busy. A `satisfies` map like `AGE_GROUP_RANK`, so a design the
// contract adds cannot be missing from the picker.
const KIT_DESIGN_PICKER_POSITION = {
  solid: 0,
  vertical_stripes: 1,
  horizontal_stripes: 2,
  diagonal_stripes: 3,
  halves: 4,
} satisfies Record<KitDesign, number>

export const KIT_DESIGN_ORDER = Object.keys(KIT_DESIGN_PICKER_POSITION) as readonly KitDesign[]

// Display order for the sex picker: not derived from the catalogue's own key order, which
// TypeScript does not guarantee for a Record built from a union type.
export const SEX_ORDER: readonly Sex[] = ['female', 'male']

/** The picker's order: right first, because it is the normal case. */
export const FOOT_ORDER: readonly PlayerFoot[] = ['right', 'left', 'both']

/** Shown in place of a value no catalogue knows. */
export const UNKNOWN_VALUE = EN ? EN_UNKNOWN_VALUE : IT_UNKNOWN_VALUE

/** The label of a domain value. A value the catalogue does not know — the backend can add one
 *  first — gives `UNKNOWN_VALUE`, never itself: showing `own_goal` is what guardrail §5 forbids. */
export function translateDomainValue<TValue extends string>(
  catalogue: Record<TValue, string>,
  value: string,
): string {
  return Object.hasOwn(catalogue, value) ? catalogue[value as TValue] : UNKNOWN_VALUE
}

/** The positions, from the catalogue's own keys: a value added to the contract cannot miss
 *  a filter chip, and the two callers that used to spell `Object.keys` are one. */
export function playerPositions(): readonly PlayerPosition[] {
  return Object.keys(PLAYER_POSITIONS) as PlayerPosition[]
}

/** A position out of a URL, or null. `Object.hasOwn` and not `in`: `?position=toString`
 *  would otherwise pass, and reach a label as a filter nobody chose. */
export function parsePlayerPosition(value: unknown): PlayerPosition | null {
  if (typeof value !== 'string') return null
  return Object.hasOwn(PLAYER_POSITIONS, value) ? (value as PlayerPosition) : null
}

/** A kind of action out of a URL, or null: the same rule as the position's. */
export function parseActionType(value: unknown): ActionType | null {
  if (typeof value !== 'string') return null
  return Object.hasOwn(ACTION_TYPES, value) ? (value as ActionType) : null
}

/** What one player plays as. `translateDomainValue`'s rule, for a catalogue of pairs:
 *  a value no catalogue knows gives UNKNOWN_VALUE, never itself. */
export function positionLabel(value: string): string {
  const known = parsePlayerPosition(value)
  return known === null ? UNKNOWN_VALUE : PLAYER_POSITIONS[known].one
}

/** The abbreviated form, for the match page's squad card: four to a row at 375px leaves no
 *  room for «Centrocampista» to sit beside its neighbour without overlapping it. */
export function positionShortLabel(value: string): string {
  const known = parsePlayerPosition(value)
  return known === null ? UNKNOWN_VALUE : PLAYER_POSITIONS[known].short
}

/** A player's preferred foot. */
export function footLabel(foot: PlayerFoot): string {
  return translateDomainValue(PLAYER_FEET, foot)
}

// The three fields a squad IS. Order and shortlist are display decisions, so they live per
// app beside the labels, unlike the backend's own values.
const AGE_GROUP_RANK = {
  seniores: 0,
  juniores: 1,
  allievi: 2,
  giovanissimi: 3,
  esordienti: 4,
  pulcini: 5,
} satisfies Record<AgeGroup, number>

export const AGE_GROUP_ORDER = Object.keys(AGE_GROUP_RANK) as readonly AgeGroup[]

export const TEAM_GENDER_ORDER: readonly TeamGender[] = ['male', 'female']

/** The age limits the FIGC prints, 0 being the seniores sentinel. Our own shortlist, not a
 *  backend enum, and a select because nobody guesses that 0 means "no limit". */
export const UNDER_AGE_OPTIONS: readonly number[] = [0, 19, 18, 17, 16, 15, 14, 13, 12, 11, 10, 9]
